import { supabase } from '@/integrations/supabase/client';
import { parseISO } from 'date-fns';
import { triggerBoardCascade } from './cascadeTrigger';
import {
  isChainGoverned,
  loadChainSnapshot,
  type ChainReplanOutcome,
} from '../../supabase/functions/_shared/rolloverChainAuthority';
import { requestServerChainReplan } from './serverChainIntent';

export interface RolloverSegment {
  id: string;
  scheduled_date: string;
  start_time: string;
  opti_finish: string | null;
  predicted_minutes: number;
  assigned_tech_id: string | null;
  parent_ticket_id: string | null;
  sequence_index: number | null;
  rollover_of_ticket_id: string | null;
  shop_id: string;
  customer_name: string | null;
  eod_rollover_warning_shown: boolean | null;
  status: string | null;
}

export interface CascadeResult {
  segmentsUpdated: number;
  newEodViolations: Array<{
    ticketId: string;
    dateStr: string;
    rolloverMinutes: number;
    customerName: string;
    techName: string;
  }>;
}

/**
 * Determines if a ticket is part of a rollover chain.
 * Core-100 R4 wave 3 (CORE-BE): one predicate (`rolloverChainMembership.ts`). It counted any non-null
 * `sequence_index` — and the column DEFAULTS to 1 — so every ticket created without naming it read as "in a chain".
 */
export { isPartOfRolloverChain } from './rolloverChainMembership';

/**
 * Fetches all tickets in a rollover chain, ordered by date then sequence
 * Returns empty array if ticket is not part of a rollover chain
 */
export async function fetchRolloverChain(ticketId: string): Promise<RolloverSegment[]> {
  // First, get the ticket to find its parent or determine if it IS a parent
  const { data: ticket, error: ticketError } = await supabase
    .from('tickets')
    .select('id, parent_ticket_id, sequence_index, rollover_of_ticket_id, shop_id')
    .eq('id', ticketId)
    .single();

  if (ticketError || !ticket) {
    console.error('[RolloverChain] Failed to fetch ticket:', ticketError);
    return [];
  }

  // Determine the root ticket ID
  let rootTicketId = ticketId;
  
  // If this ticket has a parent, use that as root
  if (ticket.parent_ticket_id) {
    rootTicketId = ticket.parent_ticket_id;
  }
  // If this ticket is a rollover of another, trace back to find the ultimate parent
  else if (ticket.rollover_of_ticket_id) {
    // Follow the chain back to find the root
    let currentId = ticket.rollover_of_ticket_id;
    let iterations = 0;
    const MAX_ITERATIONS = 10;
    
    while (iterations < MAX_ITERATIONS) {
      const { data: parentTicket } = await supabase
        .from('tickets')
        .select('id, parent_ticket_id, rollover_of_ticket_id')
        .eq('id', currentId)
        .single();
      
      if (!parentTicket) break;
      
      if (parentTicket.parent_ticket_id) {
        rootTicketId = parentTicket.parent_ticket_id;
        break;
      } else if (parentTicket.rollover_of_ticket_id) {
        currentId = parentTicket.rollover_of_ticket_id;
      } else {
        // This is the root
        rootTicketId = currentId;
        break;
      }
      iterations++;
    }
  }

  // Fetch all tickets in the chain:
  // 1. The root ticket itself
  // 2. All tickets with parent_ticket_id = rootTicketId
  // 3. All tickets with rollover_of_ticket_id pointing to any ticket in the chain
  const { data: chainTickets, error: chainError } = await supabase
    .from('tickets')
    .select('id, scheduled_date, start_time, opti_finish, predicted_minutes, assigned_tech_id, parent_ticket_id, sequence_index, rollover_of_ticket_id, shop_id, customer_name, eod_rollover_warning_shown, status')
    // CORE24-DEV-1: Include rollover_of_ticket_id leg for defensive completeness
    .or(`id.eq.${rootTicketId},parent_ticket_id.eq.${rootTicketId},rollover_of_ticket_id.eq.${rootTicketId}`)
    .order('scheduled_date', { ascending: true })
    .order('sequence_index', { ascending: true, nullsFirst: true })
    .order('start_time', { ascending: true });

  if (chainError || !chainTickets) {
    console.error('[RolloverChain] Failed to fetch chain:', chainError);
    return [];
  }

  // If we only have 1 ticket (the original), it's not a multi-day chain
  if (chainTickets.length <= 1) {
    return [];
  }

  return chainTickets as RolloverSegment[];
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE CLIENT'S DOOR TO THE ROLLOVER CHAIN AUTHORITY
 * ═══════════════════════════════════════════════════════════════════════════
 * Every decision about a chain's unresolved future — which days, what start,
 * what finish, which book share, what OptiTime, how many days — is made by
 * `supabase/functions/_shared/rolloverChainAuthority.ts`, run by the SERVER:
 * ONE planner run over the technician's dated workdays, ONE
 * `replan_rollover_chain_from_source` transaction. Core-100 R4 wave 2 (CORE-AC):
 * the browser no longer runs the planner nor calls that RPC (it is the service
 * role's only); these wrappers state the INTENT to `ticket-finish-intent`
 * (`requestServerChainReplan`) and re-cascade the lanes the server's re-plan touched.
 *
 * CSI-S2 / DMC-G2: no hours are read here at all; the authority's dated
 * workday resolver owns the day (the flat-shift fallback lives only inside it).
 */
function cascadeLanes(shopId: string, out: ChainReplanOutcome | null): void {
  if (!out || out.status !== 'applied') return;
  for (const lane of out.lanes) {
    triggerBoardCascade(shopId, lane.date, lane.techId ?? undefined).catch((err) =>
      console.warn('[RolloverChain] Post-replan cascade error:', err),
    );
  }
}

async function shopOf(ticketId: string): Promise<string | null> {
  const { data } = await supabase.from('tickets').select('shop_id').eq('id', ticketId).maybeSingle();
  return (data?.shop_id as string | undefined) ?? null;
}

/**
 * PUSHBACK: the part being worked now finishes later. Its unresolved future is
 * re-planned once from that finish (never pulled earlier). No segment the
 * planner produces ends past its dated end of day, so `newEodViolations`
 * stays empty.
 */
export async function cascadeRolloverChainAfterPushback(
  ticketId: string,
  shopId: string
): Promise<CascadeResult> {
  const result: CascadeResult = { segmentsUpdated: 0, newEodViolations: [] };
  try {
    const out = await requestServerChainReplan(ticketId, 'chain_pushback');
    if (out?.status === 'applied') result.segmentsUpdated = out.written;
    cascadeLanes(shopId, out);
  } catch (err) {
    console.error('[RolloverChain] Pushback re-plan error:', err);
  }
  return result;
}

/**
 * WORK CHANGE on a chain row (a job added, removed or edited; a DVI, canned
 * job or intake attached). Call it AFTER changing the job lines and INSTEAD of
 * writing the row's `predicted_minutes`: the chain's total is its job ledger,
 * and the authority derives every share from it. Returns false when the ticket
 * is not a multi-row chain — the caller's ordinary single-ticket path applies.
 */
export async function recomputeRolloverChainWork(ticketId: string): Promise<boolean> {
  try {
    const out = await requestServerChainReplan(ticketId, 'chain_work_change');
    if (out === null) return false;
    const shopId = await shopOf(ticketId);
    if (shopId) cascadeLanes(shopId, out);
    return true;
  } catch (err) {
    console.error('[RolloverChain] Work-change re-plan error:', err);
    return true;
  }
}

/** Is this ticket a row of a multi-row rollover chain (by lineage)? */
export async function isRolloverChainRow(ticketId: string): Promise<boolean> {
  const snap = await loadChainSnapshot(supabase, ticketId);
  if (!snap) return false;
  return snap.rows.some((r) => r.id !== snap.row.id && !r.deleted_at
    && (r.rollover_of_ticket_id === snap.row.id || r.id === snap.row.rollover_of_ticket_id));
}

/**
 * MORE FUTURE WORK after `sourceId`: work it could not do today (a run-long or
 * pause locked at its end of day). The chain's future grows — a new day when
 * it must, even from the last row — and is re-planned from the next valid day.
 */
export async function addFutureWorkToRolloverChain(sourceId: string, extraWallMinutes: number): Promise<boolean> {
  if (!(extraWallMinutes > 0)) return false;
  // Minutes of WORK (whole, at most a day), never a time: the server's planner places them.
  const out = await requestServerChainReplan(sourceId, 'chain_add_future_work', {
    extraWallMinutes: Math.min(1440, Math.max(1, Math.round(extraWallMinutes))),
  });
  const shopId = out ? await shopOf(sourceId) : null;
  if (shopId) cascadeLanes(shopId, out);
  if (out && (out.status === 'refused' || out.status === 'conflict')) {
    throw new Error(`rollover chain re-plan ${out.status}${out.status === 'refused' ? `: ${out.reason}` : ''}`);
  }
  return !!out;
}

/**
 * MOVE A CHAIN'S FRONTIER: this unresolved chain row must start no earlier
 * than `floor` (an AutoBump, an operator's date change). The row and every
 * unresolved row after it are re-planned together from the first valid
 * opportunity — never detached from the chain.
 */
export async function replanRolloverChainFromRow(
  rowId: string,
  floor: { date: string; min: number },
  opts: { mayPullEarlier?: boolean; label?: string; requireStartAtFloor?: boolean } = {},
): Promise<ChainReplanOutcome | null> {
  const out = await requestServerChainReplan(rowId, 'chain_replan_from_row', {
    floorDate: floor.date,
    floorMin: Math.max(0, Math.min(1439, Math.round(floor.min))),
    mayPullEarlier: opts.mayPullEarlier,
    requireStartAtFloor: opts.requireStartAtFloor,
    label: opts.label?.slice(0, 80),
  });
  const shopId = out ? await shopOf(rowId) : null;
  if (shopId) cascadeLanes(shopId, out);
  return out;
}

/**
 * The ticket's non-tombstoned rollover successor, if any. A ticket that
 * already continues has its future work carried: the UI must not offer to
 * carry it again (the server refuses with ROLLOVER_SUCCESSOR_EXISTS anyway).
 */
export async function liveRolloverSuccessorId(ticketId: string): Promise<string | null> {
  const { data } = await supabase
    .from('tickets')
    .select('id')
    .eq('rollover_of_ticket_id', ticketId)
    .is('deleted_at', null)
    .not('status', 'in', '("Deleted","Cancelled")')
    .limit(1);
  return ((data ?? []) as Array<{ id: string }>)[0]?.id ?? null;
}

/** Does the chain authority govern this row's future schedule? */
export async function isChainGovernedRow(ticketId: string): Promise<boolean> {
  const snap = await loadChainSnapshot(supabase, ticketId);
  return !!snap && isChainGoverned(snap.row, snap.rows);
}

/**
 * Is the ticket immediately before this one finished?
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 8G.2D.6A — RESOLVED BY IDENTITY, NOT REBUILT FROM A GENERATION NUMBER
 * ═══════════════════════════════════════════════════════════════════════════
 * This used to find the predecessor by reconstructing its identity:
 *
 *     .eq('parent_ticket_id', root).eq('sequence_index', seq - 1).single()
 *
 * Two things were wrong with that.
 *
 * First, the row already NAMES its predecessor. `rollover_of_ticket_id` is the
 * immediate predecessor, so rebuilding an identity the database already stores
 * can only ever be a weaker way of asking the same question.
 *
 * Second, it did not work. The EOD producer hardcoded `sequence_index` 1 and 2
 * on every roll, so a real three-generation chain measured [1, 1, 1, 2] and
 * that `.single()` matched THREE rows (8G.2D.6). Generations are repaired now,
 * but legacy rows written by the old producer still carry duplicates, and a
 * `.single()` over them fails on data nobody can retroactively fix.
 *
 * FAIL CLOSED. The old code treated "no predecessor row found" as
 * `isComplete: true`. A ticket that explicitly names a predecessor it cannot
 * read is not a first part — it is a ticket with broken ancestry, and
 * unlocking a technician's timer is the unsafe direction to guess in. A root is
 * identified by naming NO predecessor, not by a lookup that came back empty.
 */
export async function isPredecessorComplete(
  ticket: {
    parent_ticket_id: string | null;
    sequence_index: number | null;
    rollover_of_ticket_id?: string | null;
  },
  currentShopDate: Date
): Promise<{ isComplete: boolean; reason?: string }> {
  // A chain ROOT names no predecessor. That — not a failed lookup — is what
  // makes a ticket first.
  if (!ticket.rollover_of_ticket_id) {
    // Rows written before the predecessor pointer was universal: fall back to
    // the old "is this a rollover at all" shape rather than locking every
    // ordinary ticket in the product.
    if (!ticket.parent_ticket_id || !ticket.sequence_index || ticket.sequence_index === 1) {
      return { isComplete: true, reason: 'First part or not a rollover' };
    }
    return {
      isComplete: false,
      reason: 'This continuation does not record which part it follows. '
            + 'Someone needs to check its rollover history.',
    };
  }

  const { data: predecessor, error } = await supabase
    .from('tickets')
    .select('id, shop_id, parent_ticket_id, sequence_index, actual_minutes, predicted_minutes, scheduled_date, rollover_completion_status')
    .eq('id', ticket.rollover_of_ticket_id)
    .maybeSingle();

  if (error) {
    return { isComplete: false, reason: 'The previous part could not be read. Try again.' };
  }

  if (!predecessor) {
    // It named one, and it is not there. Do NOT unlock.
    return {
      isComplete: false,
      reason: 'The part this one follows no longer exists. '
            + 'Someone needs to resolve its rollover history.',
    };
  }

  // The named predecessor must belong to the same chain — either it IS the
  // root, or it shares the root. A pointer into another visit is malformed
  // lineage, not a finished predecessor.
  if (ticket.parent_ticket_id) {
    const sameChain =
      predecessor.id === ticket.parent_ticket_id ||
      predecessor.parent_ticket_id === ticket.parent_ticket_id;
    if (!sameChain) {
      return {
        isComplete: false,
        reason: 'The part this one follows belongs to a different visit. '
              + 'Someone needs to resolve its rollover history.',
      };
    }
  }

  // Primary check: full optiTime completed
  const actualMinutes = predecessor.actual_minutes || 0;
  if (actualMinutes >= predecessor.predicted_minutes) {
    return { isComplete: true, reason: 'Predecessor completed full optiTime' };
  }

  // Secondary check: has the day transitioned?
  if (predecessor.scheduled_date) {
    // R9-L-2: Parse as local midnight, not UTC midnight
    const predecessorDate = parseISO(predecessor.scheduled_date + 'T00:00:00');
    const { format: fmtDate } = await import('date-fns');
    const currentDate = parseISO(fmtDate(currentShopDate, 'yyyy-MM-dd') + 'T00:00:00');

    if (currentDate > predecessorDate) {
      return { isComplete: true, reason: 'Day has transitioned' };
    }
  }

  const remainingMinutes = predecessor.predicted_minutes - actualMinutes;
  return {
    isComplete: false,
    reason: `Waiting for the previous part to complete ${remainingMinutes} remaining minutes`,
  };
}
